import { useCallback, useMemo } from 'react';
import { format, formatDistanceToNow, parse, parseISO } from 'date-fns';
import { enGB, enUS } from 'date-fns/locale';
import {
    formatDateString as UtilFormatDateString,
    formatDateStringIgnoreTime as UtilFormatDateStringIgnoreTime,
    LANG_en_gb,
    LANG_en_us,
} from 'c-lib';
import useLanguageSwitcher from './useLanguageSwitcher';

// the api format
const apiDayMonthYearHourMinuteSecondFormat = 'yyyy-MM-dd HH:mm:ss';

const defaultDayMonthYearOutputFormat = 'yyyy-MM-dd';
const defaultDayMonthYearHourMinuteOutputFormat = 'yyyy-MM-dd HH:mm';
const defaultTimeFormat = 'HH:mm';

const defaultDayDisplayFormat = 'dd';
const defaultMonthDisplayFormat = 'MMM';
const defaultYearDisplayFormat = 'yyyy';
const defaultDayMonthYearDisplayFormat = `${defaultDayDisplayFormat}-${defaultMonthDisplayFormat}-${defaultYearDisplayFormat}`;
const USADayMonthYearDisplayFormat = `${defaultMonthDisplayFormat}-${defaultDayDisplayFormat}-${defaultYearDisplayFormat}`;

const DMYDateFormatting = {
    [LANG_en_gb]: defaultDayMonthYearDisplayFormat,
    [LANG_en_us]: USADayMonthYearDisplayFormat,
};

const relativeLocales = {
    [LANG_en_gb]: enGB,
    [LANG_en_us]: enUS,
};

const DMYDatePickerFormatting = {
    [LANG_en_gb]: 'dd/MM/yyyy',
    [LANG_en_us]: 'MM/dd/yyyy',
};

const DMYDateTimePickerFormatting = {
    [LANG_en_gb]: 'dd/MM/yyyy HH:mm',
    [LANG_en_us]: 'MM/dd/yyyy HH:mm',
};

/**
 * Drop a trailing UTC offset (`Z`, `+02:00`, `-05:00`, `-0500`) from an ISO datetime so it is read
 * as wall-clock time. Only the time part is touched: a date-only string's `-05` is the day.
 */
const stripUtcOffset = (date: string) => {
    const [day, time] = date.split('T');
    if (time == null) return date;
    return `${day}T${time.replace(/(Z|[+-]\d{2}(:?\d{2})?)$/i, '')}`;
};

function useDateUtils() {
    const { lang } = useLanguageSwitcher();
    const dayMonthYearFormat = useMemo(
        () => DMYDateFormatting[lang] ?? defaultDayMonthYearDisplayFormat,
        [lang],
    );
    const datePickerInputFormat = useMemo(
        () => DMYDatePickerFormatting[lang] ?? DMYDatePickerFormatting[LANG_en_gb],
        [lang],
    );
    const dateTimePickerInputFormat = useMemo(
        () => DMYDateTimePickerFormatting[lang] ?? DMYDateTimePickerFormatting[LANG_en_gb],
        [lang],
    );

    const formatDateString = useCallback<typeof UtilFormatDateString>(
        args =>
            UtilFormatDateString({
                timeFormat: defaultTimeFormat,
                dateFormatString: dayMonthYearFormat,
                ...args,
            }),
        [dayMonthYearFormat],
    );
    const formatDateStringIgnoreTime = useCallback<typeof UtilFormatDateStringIgnoreTime>(
        args =>
            UtilFormatDateStringIgnoreTime({
                dateFormatString: dayMonthYearFormat,
                ...args,
            }),
        [dayMonthYearFormat],
    );
    const formatDateStringIgnoreTimeDiff = useCallback<typeof formatDateString>(
        args =>
            formatDateString({
                ...args,
                date: typeof args.date === 'string' ? stripUtcOffset(args.date) : args.date,
            }),
        [formatDateString],
    );
    const apiFormatDateString = useCallback<typeof UtilFormatDateString>(
        args =>
            formatDateString({
                dateFormatString: defaultDayMonthYearOutputFormat,
                ...args,
            }),
        [formatDateString],
    );
    const apiFormatDateTimeString = useCallback<typeof UtilFormatDateString>(
        args =>
            formatDateString({
                dateFormatString: defaultDayMonthYearOutputFormat,
                timeFormat: defaultTimeFormat,
                removeTime: false,
                ...args,
            }),
        [formatDateString],
    );

    const formatTime = useCallback(
        // ISO8601 date
        (date: string | Date) =>
            format(typeof date === 'string' ? parseISO(date) : date, defaultTimeFormat),
        [],
    );
    const parseApiDayMonthYearHourMinuteSecondFormat = useCallback(
        (date: string, ignoreTime = false) => {
            if (ignoreTime)
                return parse(date.split(' ')[0], defaultDayMonthYearOutputFormat, new Date());
            return parse(date, apiDayMonthYearHourMinuteSecondFormat, new Date());
        },
        [],
    );
    /**
     * How long ago an API datetime was, in words: "5 minutes ago", "about 2 hours ago", "3 days
     * ago". Measured against now when called, so render it fresh (don't memoise the result).
     */
    const formatTimeAgo = useCallback(
        (date: string | Date) =>
            formatDistanceToNow(typeof date === 'string' ? parseISO(date) : date, {
                addSuffix: true,
                locale: relativeLocales[lang] ?? enGB,
            }),
        [lang],
    );
    const parseApiDayMonthYearFormat = useCallback(
        (date: string) => parse(date, defaultDayMonthYearOutputFormat, new Date()),
        [],
    );

    return useMemo(
        () => ({
            formatDateString,
            formatDateStringIgnoreTime,
            formatDateStringIgnoreTimeDiff,
            apiFormatDateString,
            apiFormatDateTimeString,
            formatTime,
            formatTimeAgo,
            timeFormatString: defaultTimeFormat,
            dayMonthYearFormat,
            dayMonthYearApiFormat: defaultDayMonthYearOutputFormat,
            dayMonthYearHourMinuteApiFormat: defaultDayMonthYearHourMinuteOutputFormat,
            datePickerInputFormat,
            dateTimePickerInputFormat,

            parseApiDayMonthYearHourMinuteSecondFormat,
            parseApiDayMonthYearFormat,
            apiDayMonthYearHourMinuteSecondFormat,
        }),
        [
            apiFormatDateString,
            apiFormatDateTimeString,
            datePickerInputFormat,
            dateTimePickerInputFormat,
            dayMonthYearFormat,
            formatDateString,
            formatDateStringIgnoreTime,
            formatDateStringIgnoreTimeDiff,
            formatTime,
            formatTimeAgo,
            parseApiDayMonthYearFormat,
            parseApiDayMonthYearHourMinuteSecondFormat,
        ],
    );
}

export default useDateUtils;
